// aiAwarenessLabels.js — the LABEL TABLE behind the AI context chips
// (WS-AI-CONTEXT-CHIPS-SHOW-ENGLISH-PAGE-NAMES-1).
//
// MEASURED (workspace i18n sweep, 2026-09-24): in a Georgian workspace the AI
// panel's chips read "Tasks", "Chat" and "25 of 30 tickets". The route chip took
// the PAGES descriptor's `page` — the English name the MODEL is told — and the
// rows chip rebuilt a sentence from the descriptor's own data keys, `${shown} of
// ${total} ${key}`. Neither is a bundle key, so no entry existed to translate and
// a per-key fix could not work: the names were DATA.
//
// This table is the bridge, and the ONLY place either chip gets its words:
//   PAGE_LABEL_KEYS        a route (the PAGES descriptor's `page`) → a bundle key
//   COLLECTION_LABEL_KEYS  a record collection (an array a descriptor's `data()`
//                          returns) → { one, many, partial } bundle keys
//
// A collection is three whole PHRASES, never a noun plus glue: `many` ("{count}
// tickets"), `one` ("1 ticket") and `partial` ("{count} of {total} tickets" —
// the page holds more rows than the wire carries). A phrase leaves word order and
// noun form to the translator. Georgian keeps the SINGULAR noun after a number
// ("25 დავალება"), which no `+ 's'` can produce — the same convention as
// workspace.aiAware.canvasItems / canvasItemsOne.
//
// The model wire is NOT read from here. `currentPage` stays the English `page`
// name: the model is told one English name in every workspace language.
//
// A PAGES route or a data collection with no row here, or a key missing from
// either bundle, fails __tests__/aiContextChipLabels.test.js.

export const PAGE_LABEL_KEYS = {
  Calendar: 'workspace.aiAware.page.calendar',
  Chat: 'workspace.aiAware.page.chat',
  Files: 'workspace.aiAware.page.files',
  History: 'workspace.aiAware.page.history',
  Meet: 'workspace.aiAware.page.meet',
  Notes: 'workspace.aiAware.page.notes',
  Tasks: 'workspace.aiAware.page.tasks',
  Terminal: 'workspace.aiAware.page.terminal',
  // The POB getting-started pages read their OWN title keys — the words of
  // their h1 and their tab title, one name per page on every surface.
  'Invite your team': 'workspace.gettingStarted.team.title',
  'Import your data': 'workspace.gettingStarted.imports.title',
  // Three built-in pages read keys they already ship: the requests page's own
  // nav name in the Resolutions app (its tab-title key reads "Customer requests
  // / Resolutions", and a chip names the page, not its section; `tickets` is in
  // the shell manifest's i18nNamespaces), the map page's tab title (one map
  // page under the workspace and the project analytics routes), and the share
  // viewer's own name for the item it shows — its tab key bakes in "/ Symbols",
  // a brand a white-label host does not carry.
  'Customer requests': 'tickets.resolutions.nav.customerRequests',
  'Analytics map': 'workspace.page.analyticsMap',
  'Shared item': 'workspace.sharedResource.untitled'
}

export const COLLECTION_LABEL_KEYS = {
  // The History app's rows: activity-log events (მოვლენა), not calendar
  // events (ღონისძიება) — their own phrase.
  activity: {
    one: 'workspace.aiAware.rows.activityOne',
    many: 'workspace.aiAware.rows.activity',
    partial: 'workspace.aiAware.rows.activityOfTotal'
  },
  channels: {
    one: 'workspace.aiAware.rows.channelsOne',
    many: 'workspace.aiAware.rows.channels',
    partial: 'workspace.aiAware.rows.channelsOfTotal'
  },
  columns: {
    one: 'workspace.aiAware.rows.columnsOne',
    many: 'workspace.aiAware.rows.columns',
    partial: 'workspace.aiAware.rows.columnsOfTotal'
  },
  documents: {
    one: 'workspace.aiAware.rows.documentsOne',
    many: 'workspace.aiAware.rows.documents',
    partial: 'workspace.aiAware.rows.documentsOfTotal'
  },
  events: {
    one: 'workspace.aiAware.rows.eventsOne',
    many: 'workspace.aiAware.rows.events',
    partial: 'workspace.aiAware.rows.eventsOfTotal'
  },
  files: {
    one: 'workspace.aiAware.rows.filesOne',
    many: 'workspace.aiAware.rows.files',
    partial: 'workspace.aiAware.rows.filesOfTotal'
  },
  notes: {
    one: 'workspace.aiAware.rows.notesOne',
    many: 'workspace.aiAware.rows.notes',
    partial: 'workspace.aiAware.rows.notesOfTotal'
  },
  rooms: {
    one: 'workspace.aiAware.rows.roomsOne',
    many: 'workspace.aiAware.rows.rooms',
    partial: 'workspace.aiAware.rows.roomsOfTotal'
  },
  tickets: {
    one: 'workspace.aiAware.rows.ticketsOne',
    many: 'workspace.aiAware.rows.tickets',
    partial: 'workspace.aiAware.rows.ticketsOfTotal'
  }
}

// A development build, or any *.localhost host. The same RUNTIME rule as the
// module override in workspaceProjects.js: it survives a production bundle,
// where `process.env.NODE_ENV` is inlined and its branch is dead-code stripped,
// and it never fires on a real deployment.
const _isDev = () => {
  if (process.env.NODE_ENV === 'development') return true
  try {
    const h = typeof location !== 'undefined' && location.hostname
    return h === 'localhost' || (typeof h === 'string' && h.endsWith('.localhost'))
  } catch (_) {
    return false
  }
}

// A route or a collection this table does not know still gets a chip — the
// raw name, never a blank — but a missing row is a bug to fix, not a state to
// live with. So in DEV the fallback says so, ONCE per name: the awareness set is
// re-derived on every render, and a line per render would bury the console.
// Returns whether it logged (the test reads it).
const _warned = new Set()
export const warnUnlabelledOnce = (kind, name) => {
  if (!_isDev()) return false
  const id = kind + ':' + name
  if (_warned.has(id)) return false
  _warned.add(id)
  console.warn(
    `[ai-context] no label for ${kind} "${name}" — the chip falls back to its raw name. ` +
      'Add a row to functions/aiAwarenessLabels.js and both workspace.aiAware.* bundles.'
  )
  return true
}

// `say(key)` resolves a bundle key: the element's polyglot in the browser, the
// shipped English on the headless path (pageContext.js `_say`).

// The route chip's words. An unknown route falls back to its own name.
export const routeLabel = (page, say) => {
  const key = PAGE_LABEL_KEYS[page]
  if (!key) {
    warnUnlabelledOnce('route', page)
    return page
  }
  return say(key)
}

// One collection's phrase: "1 ticket", "3 tickets" or "25 of 30 tickets".
// `partial` wins whenever the page holds more rows than the wire carries; else
// `one` for a single row and `many` for the rest.
const _collectionPhrase = (collection, shown, total, say) => {
  const keys = COLLECTION_LABEL_KEYS[collection]
  if (!keys) {
    warnUnlabelledOnce('collection', collection)
    return `${shown} ${collection}`
  }
  const key = total > shown ? keys.partial : shown === 1 ? keys.one : keys.many
  return say(key).replace('{count}', String(shown)).replace('{total}', String(total))
}

// The rows chip: every non-empty array in a page descriptor's `data(root)`,
// one phrase each. `truncated` is true when any collection holds more rows on
// root than the descriptor sends. A collection is counted at `root[k]` unless
// the descriptor's `rootKeys` says its rows live under another root key
// (Files returns `files` and reads `root.fileNodes`); without that mapping the
// total falls back to what was sent and a cut-off list reads as complete.
export const rowsSummary = (data, root, say, rootKeys = {}) => {
  const parts = []
  let truncated = false
  for (const k of Object.keys(data)) {
    const v = data[k]
    if (!Array.isArray(v)) continue
    const source = root[rootKeys[k] || k]
    const total = Array.isArray(source) ? source.length : v.length
    if (total > v.length) truncated = true
    if (v.length) parts.push(_collectionPhrase(k, v.length, total, say))
  }
  return { title: parts.join(', '), truncated }
}
